import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { format } from 'date-fns';
import { Plus, RotateCcw, Search, Trash2 } from 'lucide-react';
import '@/styles/common/empty-state.css';
import '@/styles/ipe/workbench/IpeWorkflowTasksPage.css';
import '@/styles/ipe/workbench/WfTasksGoodsListPage.css';
import IpeToolbarSelect from '@/pages/components/IpeToolbarSelect';
import TaskProgressBar from './TaskProgressBar';
import WfTasksPagination from './WfTasksPagination';
import { IpeImagePreviewOverlay, useIpeImagePreview } from './IpeImagePreviewOverlay';
import { getBatchTaskProgress } from '@/pages/ipe/workbench/utils/taskProgress';
import { isRetryableTaskStatus } from '@/pages/ipe/workbench/utils/ipeTaskActions';
import WorkbenchPreviewImage from '@/components/ipe/WorkbenchPreviewImage';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';

function statusFilterOptions(t) {
  return [
    { value: '', label: t('studioChrome.statusAll') },
    { value: 'waiting', label: t('studioChrome.statusWaiting') },
    { value: 'executing', label: t('studioChrome.statusRunning') },
    { value: 'success', label: t('studioChrome.statusDone') },
    { value: 'partial', label: t('studioChrome.statusStopped') },
  ];
}

function taskStatusVariant(status) {
  if (status === 'succeed' || status === 'success' || status === 'completed') return 'success';
  if (status === 'waiting') return 'waiting';
  if (status === 'failed') return 'failed';
  if (status === 'partial' || status === 'canceled' || status === 'cancelled') return 'partial';
  return 'processing';
}

function taskStatusLabel(status, t) {
  if (status === 'waiting') return t('studioChrome.statusWaiting');
  if (status === 'executing' || status === 'processing' || status === 'pending' || status === 'NEW') return t('studioChrome.statusRunning');
  if (status === 'succeed' || status === 'success' || status === 'completed') return t('studioChrome.statusDone');
  if (status === 'partial' || status === 'canceled') return t('studioChrome.statusStopped');
  if (status === 'failed') return t('studioChrome.statusFailed');
  return t('studioChrome.statusRunning');
}

function displayOperator(raw, t) {
  const name = String(raw || '').trim();
  if (!name || name === '系统') return t('studioChrome.system');
  return name;
}

function taskRowClass(status) {
  const variant = taskStatusVariant(status);
  if (variant === 'success') return 'wf-tasks-table__row wf-tasks-table__row--done';
  if (variant === 'processing') return 'wf-tasks-table__row wf-tasks-table__row--running';
  if (variant === 'failed') return 'wf-tasks-table__row';
  return 'wf-tasks-table__row';
}

export default function GoodsSharedContent({
  filterInputs,
  onInputChange,
  onQuery,
  tasks,
  showPresetButton = false,
  onCreatePreset = () => {},
  onCreateTask,
  onViewDetail,
  DateRangePickerComponent,
  showDownloadButton = false,
  onDownload,
  downloadingTaskId = '',
  onTerminateTask,
  onRetryTask,
  retryingTaskId = '',
  terminatingTaskId = '',
  plainTextTaskActions = false,
  currentPage = 1,
  pageSize = 20,
  onPageChange,
  onPageSizeChange,
  isLoading = false,
  totalTasks = null,
  isCropMode = false,
  getTaskPreviewUrl = () => '',
  tableClassName = '',
  onlyCreateTaskInteractive = false,
  taskExtraColumnHeader = '',
  renderTaskExtraCell = null,
  batchCellLayout = 'stack',
  renderInfoCell = null,
  enableRowSelection = false,
  onDeleteSelected,
  onDeleteTask,
  canDeleteTask = () => true,
  selectionResetKey = 0,
  isBulkDeleting = false,
  embedded = false,
  emptyTitle = i18n.t('workbench:goodsShared.emptyTitle'),
  emptyDescription = i18n.t('workbench:goodsShared.emptyDescription'),
  subAccountPlaceholder = i18n.t('workbench:studioChrome.batchNoPh'),
  subAccountClearAriaLabel = i18n.t('workbench:goodsShared.clearBatchNo'),
  subAccountFallback = i18n.t('workbench:goodsShared.untitledBatch'),
  batchCellIdOnly = false,
  filterToolbarVariant = 'default',
  onResetFilters,
  hideTaskCountColumn = false,
  enablePreviewClick = false,
  showOperatorFilter = false,
  operatorFilterOptions = [{ value: '', label: i18n.t('workbench:goodsShared.allAssignees') }],
}) {
  const { t } = useTranslation('workbench');
  const toolbarLocked = onlyCreateTaskInteractive;
  const usePlainTerminateActions = plainTextTaskActions || typeof onTerminateTask === 'function';
  const [selectedTaskIds, setSelectedTaskIds] = useState(() => new Set());
  const selectAllCheckboxRef = useRef(null);
  const {
    previewImage,
    previewScale,
    setPreviewScale,
    previewContainerRef,
    openPreview,
    closePreview,
  } = useIpeImagePreview();
  const totalItems = totalTasks !== null ? totalTasks : tasks.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const hasExtraColumn = Boolean(taskExtraColumnHeader && typeof renderTaskExtraCell === 'function');
  const shouldHideTaskCountColumn = hideTaskCountColumn || (isCropMode && typeof renderInfoCell === 'function');
  const baseColCount = (hasExtraColumn ? 6 : 5) - (shouldHideTaskCountColumn ? 1 : 0);
  const tableColCount = enableRowSelection ? baseColCount + 1 : baseColCount;
  const hasRowSelection = selectedTaskIds.size > 0;
  const pageTaskIds = tasks.map((task) => String(task.id)).filter(Boolean);
  const pageTaskIdsKey = pageTaskIds.join('|');
  const allPageSelected = pageTaskIds.length > 0 && pageTaskIds.every((id) => selectedTaskIds.has(id));
  const somePageSelected = pageTaskIds.some((id) => selectedTaskIds.has(id));

  const previewNavItems = useMemo(() => {
    if (!enablePreviewClick) return [];
    return tasks
      .map((task, index) => {
        const url = String(getTaskPreviewUrl(task) || '').trim();
        if (!url) return null;
        return {
          url,
          displayUrl: task.previewDisplayUrl || task.previewViewUrl || '',
          viewUrl: task.previewViewUrl || '',
          name: task.subAccount || task.id || `任务 ${index + 1}`,
          taskId: String(task.id),
        };
      })
      .filter(Boolean);
  }, [enablePreviewClick, tasks, getTaskPreviewUrl]);

  const previewNavIndex = useMemo(() => {
    if (!previewImage?.taskId) return -1;
    return previewNavItems.findIndex((item) => item.taskId === String(previewImage.taskId));
  }, [previewNavItems, previewImage?.taskId]);

  const handleOpenTaskPreview = useCallback((task, previewUrl) => {
    if (!enablePreviewClick || !previewUrl) return;
    openPreview(previewUrl, task.subAccount || task.id || t('studioChrome.taskPreview'), {
      displayUrl: task.previewDisplayUrl || task.previewViewUrl,
      viewUrl: task.previewViewUrl,
      taskId: String(task.id),
    });
  }, [enablePreviewClick, openPreview, t]);

  const handlePreviewNavigate = useCallback((delta) => {
    const nextIndex = previewNavIndex + delta;
    if (nextIndex < 0 || nextIndex >= previewNavItems.length) return;
    const item = previewNavItems[nextIndex];
    openPreview(item.url, item.name, {
      displayUrl: item.displayUrl,
      viewUrl: item.viewUrl,
      taskId: item.taskId,
    });
  }, [openPreview, previewNavIndex, previewNavItems]);

  const toggleTaskSelect = useCallback((taskId) => {
    const id = String(taskId);
    setSelectedTaskIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAllOnPage = useCallback(() => {
    setSelectedTaskIds((prev) => {
      const next = new Set(prev);
      if (allPageSelected) {
        pageTaskIds.forEach((id) => next.delete(id));
      } else {
        pageTaskIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }, [allPageSelected, pageTaskIds]);

  /** 终止 / 重试 / 删除 / 下载互斥：任一进行中则整表操作按钮一并禁用 */
  const taskActionBusy = Boolean(
    isBulkDeleting || terminatingTaskId || retryingTaskId || downloadingTaskId,
  );

  const handleBulkDelete = useCallback(() => {
    if (
      !hasRowSelection
      || typeof onDeleteSelected !== 'function'
      || toolbarLocked
      || taskActionBusy
    ) return;
    onDeleteSelected([...selectedTaskIds]);
  }, [hasRowSelection, onDeleteSelected, selectedTaskIds, taskActionBusy, toolbarLocked]);

  useEffect(() => {
    if (!enableRowSelection || !selectionResetKey) return;
    setSelectedTaskIds(new Set());
  }, [enableRowSelection, selectionResetKey]);

  useEffect(() => {
    if (!enableRowSelection) return;
    const valid = new Set(pageTaskIdsKey ? pageTaskIdsKey.split('|') : []);
    setSelectedTaskIds((prev) => {
      const next = new Set([...prev].filter((id) => valid.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [enableRowSelection, pageTaskIdsKey]);

  useEffect(() => {
    if (!enableRowSelection || !selectAllCheckboxRef.current) return;
    selectAllCheckboxRef.current.indeterminate = somePageSelected && !allPageSelected;
  }, [enableRowSelection, somePageSelected, allPageSelected]);

  useEffect(() => {
    if (!onPageChange || totalItems === 0) return;
    const tp = Math.ceil(totalItems / pageSize);
    if (tp > 0 && currentPage > tp) {
      onPageChange(tp);
    }
  }, [totalItems, pageSize, currentPage, onPageChange]);

  const tableClassNames = [
    'wf-tasks-table',
    'wf-goods-tasks-table',
    shouldHideTaskCountColumn ? 'wf-goods-tasks-table--no-count' : '',
    enableRowSelection ? 'wf-goods-tasks-table--has-select' : '',
    tableClassName,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <section
      className={`wf-tasks-page wf-goods-tasks-page${embedded ? ' wf-tasks-page--embedded' : ''}${
        isLoading ? ' wf-goods-tasks-page--loading' : ''
      }`}
    >
      <div className="wf-tasks-page__shell">
        <header className="wf-tasks-toolbar" aria-label={t('studioChrome.toolbarAria')}>
          <div className="wf-tasks-toolbar__primary">
            {typeof onCreateTask === 'function' && (
              <button type="button" className="wf-tasks-btn wf-tasks-btn--new-task" onClick={onCreateTask} disabled={toolbarLocked}>
                <Plus size={14} strokeWidth={2.5} aria-hidden="true" />
                {t('studioChrome.newTask')}
              </button>
            )}
            {enableRowSelection && filterToolbarVariant !== 'icon' ? (
              <button
                type="button"
                className="wf-tasks-btn wf-tasks-btn--ghost"
                disabled={toolbarLocked || isLoading || taskActionBusy || !hasRowSelection}
                title={hasRowSelection ? undefined : t('studioChrome.selectFirst')}
                onClick={handleBulkDelete}
              >
                {isBulkDeleting ? t('studioChrome.deleting') : t('studioChrome.delete')}
              </button>
            ) : null}
            {showPresetButton && (
              <button type="button" className="wf-tasks-btn wf-tasks-btn--new-task" onClick={onCreatePreset} disabled={toolbarLocked}>
                <Plus size={14} strokeWidth={2.5} aria-hidden="true" />
                {t('studioChrome.newPreset')}
              </button>
            )}
          </div>
          <div className="wf-tasks-toolbar__filters">
            {showOperatorFilter ? (
              <div className="wf-tasks-toolbar__field wf-tasks-toolbar__field--operator">
                <IpeToolbarSelect
                  id="wf-tasks-operator-select"
                  className="ipe-owner-filter-select"
                  value={filterInputs.operatorUserId || ''}
                  onChange={(v) => onInputChange('operatorUserId', v)}
                  options={operatorFilterOptions}
                  ariaLabel={t('studioChrome.ownerAria')}
                  variant="light"
                  clearable={false}
                  disabled={toolbarLocked}
                />
              </div>
            ) : null}
            <div className="wf-tasks-toolbar__field wf-tasks-toolbar__field--clearable">
              <input
                id="wf-goods-sub-account"
                type="text"
                className="wf-tasks-filter wf-tasks-toolbar__control"
                placeholder={subAccountPlaceholder}
                value={filterInputs.subAccount}
                onChange={(e) => onInputChange('subAccount', e.target.value)}
                disabled={toolbarLocked}
              />
              {String(filterInputs.subAccount || '').trim() !== '' && !toolbarLocked ? (
                <button
                  type="button"
                  className="wf-tasks-toolbar__clear"
                  onClick={() => onInputChange('subAccount', '')}
                  aria-label={subAccountClearAriaLabel}
                >
                  ×
                </button>
              ) : null}
            </div>
            <div className="wf-tasks-toolbar__field">
              <IpeToolbarSelect
                id="wf-goods-status-select"
                value={filterInputs.status}
                onChange={(v) => onInputChange('status', v)}
                options={statusFilterOptions(t)}
                ariaLabel={t('studioChrome.taskStatusAria')}
                disabled={toolbarLocked}
              />
            </div>
            <div className="wf-tasks-toolbar__field wf-tasks-toolbar__field--date">
              {React.isValidElement(DateRangePickerComponent)
                ? React.cloneElement(DateRangePickerComponent, { disabled: toolbarLocked })
                : DateRangePickerComponent}
            </div>
            {filterToolbarVariant === 'icon' ? (
              <div className="wf-tasks-toolbar__icon-actions">
                {enableRowSelection ? (
                  <button
                    type="button"
                    className={`wf-tasks-toolbar__icon-btn${
                      hasRowSelection ? ' wf-tasks-toolbar__icon-btn--danger' : ''
                    }`}
                    disabled={toolbarLocked || isLoading || taskActionBusy || !hasRowSelection}
                    onClick={handleBulkDelete}
                    aria-label={isBulkDeleting ? t('studioChrome.deleting') : t('studioChrome.bulkDelete')}
                    title={hasRowSelection ? t('studioChrome.bulkDelete') : t('studioChrome.selectFirst')}
                  >
                    <Trash2 size={16} strokeWidth={2} aria-hidden="true" />
                  </button>
                ) : null}
                {typeof onResetFilters === 'function' ? (
                  <button
                    type="button"
                    className="wf-tasks-toolbar__icon-btn"
                    onClick={onResetFilters}
                    disabled={isLoading || toolbarLocked}
                    aria-label={t('studioChrome.resetFilters')}
                    title={t('studioChrome.reset')}
                  >
                    <RotateCcw size={16} strokeWidth={2} aria-hidden="true" />
                  </button>
                ) : null}
                <button
                  type="button"
                  className="wf-tasks-toolbar__icon-btn"
                  onClick={onQuery}
                  disabled={isLoading || toolbarLocked}
                  aria-label={isLoading ? t('studioChrome.querying') : t('studioChrome.query')}
                  title={t('studioChrome.query')}
                >
                  <Search size={16} strokeWidth={2} aria-hidden="true" />
                </button>
              </div>
            ) : (
              <>
                {typeof onResetFilters === 'function' ? (
                  <button
                    type="button"
                    className="wf-tasks-btn wf-tasks-btn--ghost"
                    onClick={onResetFilters}
                    disabled={isLoading || toolbarLocked}
                  >
                    {t('studioChrome.reset')}
                  </button>
                ) : null}
                <button
                  type="button"
                  className="wf-tasks-btn wf-tasks-btn--query"
                  onClick={onQuery}
                  disabled={isLoading || toolbarLocked}
                >
                  {isLoading ? t('studioChrome.querying') : t('studioChrome.query')}
                </button>
              </>
            )}
          </div>
        </header>

        <div className="wf-tasks-table-wrap" role="region" aria-live="polite">
          <table className={tableClassNames}>
            <thead>
              <tr>
                {enableRowSelection ? (
                  <th className="wf-goods-tasks-table__th-check wf-goods-tasks-table__td-check" scope="col" aria-label={t('studioChrome.selectPage')}>
                    <input
                      ref={selectAllCheckboxRef}
                      type="checkbox"
                      checked={allPageSelected}
                      onChange={toggleSelectAllOnPage}
                      disabled={toolbarLocked || tasks.length === 0}
                      aria-label={t('studioChrome.selectPage')}
                    />
                  </th>
                ) : null}
                <th scope="col" className="wf-goods-tasks-table__th-preview">{t('studioChrome.preview')}</th>
                <th scope="col" className="wf-goods-tasks-table__th-batch">{t('studioChrome.batch')}</th>
                {!shouldHideTaskCountColumn ? (
                  <th scope="col" className="wf-goods-tasks-table__th-count">{t('studioChrome.taskCount')}</th>
                ) : null}
                {hasExtraColumn ? (
                  <th scope="col" className="wf-goods-tasks-table__th-extra">{taskExtraColumnHeader}</th>
                ) : null}
                <th scope="col" className="wf-goods-tasks-table__th-info">{t('studioChrome.info')}</th>
                <th scope="col" className="wf-goods-tasks-table__th-actions">{t('studioChrome.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && tasks.length === 0 ? (
                <tr>
                  <td colSpan={tableColCount} className="wf-tasks-table__loading">
                    <div className="wf-tasks-table__loading-inner" role="status" aria-live="polite">
                      <div className="funding-table-loading-spinner" aria-hidden="true" />
                      <span>{t('studioChrome.loading')}</span>
                    </div>
                  </td>
                </tr>
              ) : !isLoading && tasks.length === 0 ? (
                <tr>
                  <td colSpan={tableColCount} className="wf-tasks-table__empty">
                    <div className="funding-empty-state">
                      <div className="funding-empty-state__icon">
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M9 11H15M9 15H15M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H12.5858C12.851 3 13.1054 3.10536 13.2929 3.29289L18.7071 8.70711C18.8946 8.89464 19 9.149 19 9.41421V19C19 20.1046 18.1046 21 17 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M13 3V9H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                      <div className="funding-empty-state__text">
                        <p className="funding-empty-state__title">{emptyTitle}</p>
                        <p className="funding-empty-state__description">{emptyDescription}</p>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                tasks.map((task) => {
                  const rowPreviewUrl = getTaskPreviewUrl(task);
                  const statusVariant = taskStatusVariant(task.status);
                  return (
                    <tr key={task.id} className={taskRowClass(task.status)} data-history-task-id={String(task.id)}>
                      {enableRowSelection ? (
                        <td className="wf-goods-tasks-table__td-check">
                          <input
                            type="checkbox"
                            checked={selectedTaskIds.has(String(task.id))}
                            onChange={() => toggleTaskSelect(task.id)}
                            disabled={toolbarLocked}
                            aria-label={`${t('studioChrome.selectTask')} ${task.subAccount || task.id}`}
                          />
                        </td>
                      ) : null}
                      <td className="wf-goods-tasks-table__td-preview">
                        {rowPreviewUrl ? (
                          enablePreviewClick ? (
                            <button
                              type="button"
                              className="wf-goods-tasks-table__thumb-btn"
                              onClick={() => handleOpenTaskPreview(task, rowPreviewUrl)}
                              aria-label={`${t('studioChrome.zoomTask')} ${task.subAccount || t('studioChrome.taskPreview')}`}
                            >
                              <WorkbenchPreviewImage
                                url={rowPreviewUrl}
                                displayUrl={task.previewDisplayUrl || task.previewViewUrl}
                                viewUrl={task.previewViewUrl}
                                loading="eager"
                                alt={t('studioChrome.taskPreview')}
                                className="wf-goods-tasks-table__thumb"
                              />
                            </button>
                          ) : (
                            <div className="wf-goods-tasks-table__preview-frame">
                              <WorkbenchPreviewImage
                                url={rowPreviewUrl}
                                displayUrl={task.previewDisplayUrl || task.previewViewUrl}
                                viewUrl={task.previewViewUrl}
                                loading="eager"
                                alt={t('studioChrome.taskPreview')}
                                className="wf-goods-tasks-table__thumb"
                              />
                            </div>
                          )
                        ) : (
                          <div className="wf-goods-tasks-table__thumb--placeholder">
                            {isCropMode ? t('studioChrome.none') : t('studioChrome.noPreview')}
                          </div>
                        )}
                      </td>
                      <td className="wf-goods-tasks-table__td-batch">
                        {batchCellIdOnly ? (
                          <span className="wf-goods-tasks-table__batch-name">{task.subAccount || (subAccountFallback === '未命名批次' ? t('studioChrome.unnamedBatch') : subAccountFallback)}</span>
                        ) : batchCellLayout === 'meta' ? (
                          <div className="wf-tasks-batch-cell">
                            <span className="wf-tasks-batch">{task.id}</span>
                            <span className="wf-goods-tasks-table__batch-name">{task.subAccount || (subAccountFallback === '未命名批次' ? t('studioChrome.unnamedBatch') : subAccountFallback)}</span>
                          </div>
                        ) : (
                          <>
                            <span className="wf-tasks-batch">{task.id}</span>
                            <span className="wf-goods-tasks-table__batch-name">{task.subAccount || (subAccountFallback === '未命名批次' ? t('studioChrome.unnamedBatch') : subAccountFallback)}</span>
                          </>
                        )}
                      </td>
                      {!shouldHideTaskCountColumn ? (
                        <td className="wf-goods-tasks-table__td-count">
                          <div className="wf-goods-tasks-table__count-cell">
                            <span className="wf-goods-tasks-table__count-total">{t('studioChrome.total')}{task.total ?? task.count ?? 0}</span>
                            <span className="wf-goods-tasks-table__count-success">{t('studioChrome.success')}{task.successCount ?? 0}</span>
                            {typeof task.failedCount === 'number' && task.failedCount > 0 ? (
                              <span className="wf-goods-tasks-table__count-failed">{t('studioChrome.failedCount')}{task.failedCount}</span>
                            ) : null}
                          </div>
                        </td>
                      ) : null}
                      {hasExtraColumn ? <td>{renderTaskExtraCell(task)}</td> : null}
                      <td className="wf-goods-tasks-table__td-info">
                        {typeof renderInfoCell === 'function' ? (
                          renderInfoCell(task)
                        ) : (
                          <div className="wf-goods-tasks-table__info-cell">
                            <span>
                              {t('studioChrome.createdAt')}
                              {task.createdAt
                                ? typeof task.createdAt === 'string' && task.createdAt.includes('T')
                                  ? format(new Date(task.createdAt), 'yyyy-MM-dd HH:mm:ss')
                                  : task.createdAt
                                : '—'}
                            </span>
                            <span>{t('studioChrome.operator')}{displayOperator(task.operator || task.createdBy || task.creator, t)}</span>
                            <span>
                              {t('studioChrome.status')}
                              <span className={`wf-tasks-status wf-tasks-status--${statusVariant}`}>
                                {taskStatusLabel(task.status, t)}
                              </span>
                            </span>
                            {!isCropMode && (() => {
                              const prog = getBatchTaskProgress(task);
                              return prog.show ? (
                                <TaskProgressBar
                                  percent={prog.percent}
                                  label={prog.label}
                                  detail={prog.detail}
                                  indeterminate={prog.indeterminate}
                                />
                              ) : null;
                            })()}
                          </div>
                        )}
                      </td>
                      <td className="wf-goods-tasks-table__td-actions">
                        <div className="wf-tasks-actions">
                          <button
                            type="button"
                            className="wf-tasks-actions__link"
                            onClick={() => !toolbarLocked && onViewDetail && onViewDetail(task)}
                            disabled={toolbarLocked}
                          >
                            {t('studioChrome.viewDetail')}
                          </button>
                          {showDownloadButton ? (
                            <button
                              type="button"
                              className="wf-tasks-actions__link"
                              disabled={toolbarLocked || taskActionBusy}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (toolbarLocked || taskActionBusy) return;
                                if (typeof onDownload === 'function') onDownload(task);
                              }}
                            >
                              {String(downloadingTaskId) === String(task.id) ? t('studioChrome.downloading') : t('studioChrome.download')}
                            </button>
                          ) : null}
                          {task.status === 'waiting' ||
                          task.status === 'processing' ||
                          task.status === 'executing' ||
                          task.status === 'pending' ? (
                            <button
                              type="button"
                              className={`wf-tasks-actions__link${
                                usePlainTerminateActions ? '' : ' wf-tasks-actions__link--danger'
                              }`}
                              disabled={toolbarLocked || taskActionBusy}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (toolbarLocked || taskActionBusy) return;
                                if (onTerminateTask) onTerminateTask(task);
                              }}
                            >
                              {String(terminatingTaskId) === String(task.id)
                                ? t('studioChrome.terminating')
                                : (task.status === 'waiting' ? t('studioChrome.cancelTask') : t('studioChrome.terminate'))}
                            </button>
                          ) : null}
                          {typeof onRetryTask === 'function' && isRetryableTaskStatus(task.status) ? (
                            <button
                              type="button"
                              className="wf-tasks-actions__link"
                              disabled={toolbarLocked || taskActionBusy}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (toolbarLocked || taskActionBusy) return;
                                onRetryTask(task);
                              }}
                            >
                              {String(retryingTaskId) === String(task.id) ? t('studioChrome.retrying') : t('studioChrome.retry')}
                            </button>
                          ) : null}
                          {typeof onDeleteTask === 'function' && canDeleteTask(task) ? (
                            <button
                              type="button"
                              className={`wf-tasks-actions__link${
                                usePlainTerminateActions ? '' : ' wf-tasks-actions__link--danger'
                              }`}
                              disabled={toolbarLocked || taskActionBusy}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (toolbarLocked || taskActionBusy) return;
                                onDeleteTask(task);
                              }}
                            >
                              {t('studioChrome.delete')}
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <WfTasksPagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
          pageSize={pageSize}
          totalItems={totalItems}
          showPageSize={typeof onPageSizeChange === 'function'}
          onPageSizeChange={
            typeof onPageSizeChange === 'function'
              ? (size) => {
                onPageSizeChange(size);
                onPageChange?.(1);
              }
              : undefined
          }
          ariaLabel={t('goodsShared.pagerAria')}
        />
      </div>

      {enablePreviewClick ? (
        <IpeImagePreviewOverlay
          preview={previewImage}
          scale={previewScale}
          setScale={setPreviewScale}
          containerRef={previewContainerRef}
          onClose={closePreview}
          navList={previewNavItems}
          navIndex={previewNavIndex}
          onNavigate={handlePreviewNavigate}
        />
      ) : null}
    </section>
  );
}
